import { TablePaginationConfig, Tag } from 'antd'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { ActionEnum, getExpectNumber } from '../../../common'
import {
  ColumnActionHeader,
  ColumnInputHeader,
  ColumnInputRangeHeader,
  ColumnSelectHeader,
  NumberFormat,
  PageTitleComponent,
  TableActionColumnButtonComponent,
  TableComponent,
} from '../../../components'
import { translate } from '../../../languages'
import { CardEntity } from '../../../models'
import { RootState } from '../../../stores'
import {
  loadCards,
  setAction,
  setCardFilter,
  setSelectedCardId,
  setShowSearchDrawer,
} from '../../../stores/actions'
import {
  ManageCardModalComponent,
  PayInModalComponent,
  SearchCardDrawerComponent,
} from './components'

export const CardPage: React.FC = () => {
  const dispatch = useDispatch()
  const { language } = useSelector((state: RootState) => state.language)
  const { management } = useSelector((state: RootState) => state.common)
  const { cards, isCardsLoaded, cardFilter, totalCards, showPayInModal } =
    useSelector((state: RootState) => state.card)
  const isDesktopScreen = useMediaQuery({ minWidth: '1300px' })
  const [filter, setFilter] = React.useState(cardFilter)

  React.useEffect(() => {
    if (isDesktopScreen) {
      dispatch(setShowSearchDrawer(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktopScreen])

  React.useEffect(() => {
    dispatch(loadCards())
    // eslint-disable-next-line
  }, [])

  React.useEffect(() => {
    setFilter(cardFilter)
    dispatch(loadCards())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardFilter])

  const columns = React.useMemo(
    () => [
      {
        dataIndex: 'id',
        title: <span>{translate('card.id', language)}</span>,
        width: 150,
      },
      {
        title: (
          <ColumnInputHeader
            value={filter.customerName}
            titleKey="common.customer"
            onChange={(e) =>
              setFilter({ ...filter, customerName: e.target.value })
            }
          />
        ),
        render: (_: any, card: CardEntity) => card.customer?.name,
        width: 120,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="common.accountsPayable"
            fromValue={filter.accountsPayableFrom}
            toValue={filter.accountsPayableTo}
            fromKey="common.from"
            toKey="common.to"
            onFromChange={(e) =>
              setFilter({
                ...filter,
                accountsPayableFrom: e ? parseInt(e.target.value) : undefined,
              })
            }
            onToChange={(e) =>
              setFilter({
                ...filter,
                accountsPayableTo: e ? parseInt(e.target.value) : undefined,
              })
            }
          />
        ),
        render: (_: any, card: CardEntity) => (
          <NumberFormat
            value={card.accountsPayable}
            suffixKey="common.moneyType"
          />
        ),
        width: 150,
      },
      {
        title: (
          <ColumnInputRangeHeader
            titleKey="card.availableBalance"
            fromValue={filter.availableBalanceFrom}
            toValue={filter.availableBalanceTo}
            fromKey="common.from"
            toKey="common.to"
            onFromChange={(e) =>
              setFilter({
                ...filter,
                availableBalanceFrom: e ? parseInt(e.target.value) : undefined,
              })
            }
            onToChange={(e) =>
              setFilter({
                ...filter,
                availableBalanceTo: e ? parseInt(e.target.value) : undefined,
              })
            }
          />
        ),
        render: (_: any, card: CardEntity) => (
          <NumberFormat
            value={card.availableBalance}
            suffixKey="common.moneyType"
          />
        ),
        width: 150,
      },
      {
        title: (
          <ColumnSelectHeader
            titleKey="common.status"
            values={filter.statuses}
            options={['activated', 'deactivated'].map((item: any) => ({
              value: item,
              name: translate(`card.status.${item}`, language),
            }))}
            onChange={(e) =>
              setFilter({
                ...filter,
                statuses: e ? e.map((item: any) => item.toString()) : [],
              })
            }
          />
        ),
        width: 70,
        render: (_: any, card: CardEntity) => (
          <Tag color={card.active ? 'success' : 'default'}>
            {translate(
              `card.status.${card.active ? 'activated' : 'deactivated'}`,
              language
            ).toUpperCase()}
          </Tag>
        ),
      },
      {
        title: (
          <ColumnActionHeader
            onSearch={() => {
              dispatch(
                setCardFilter({
                  ...filter,
                  pageIndex: 1,
                  accountsPayableFrom: getExpectNumber(
                    true,
                    filter.accountsPayableFrom,
                    filter.accountsPayableTo
                  ),
                  accountsPayableTo: getExpectNumber(
                    false,
                    filter.accountsPayableFrom,
                    filter.accountsPayableTo
                  ),
                  availableBalanceFrom: getExpectNumber(
                    true,
                    filter.availableBalanceFrom,
                    filter.availableBalanceTo
                  ),
                  availableBalanceTo: getExpectNumber(
                    false,
                    filter.availableBalanceFrom,
                    filter.availableBalanceTo
                  ),
                })
              )
            }}
          />
        ),
        render: (_: any, card: CardEntity) => (
          <TableActionColumnButtonComponent
            onViewButtonClick={() => {
              dispatch(setSelectedCardId(card.id))
              dispatch(setAction(ActionEnum.VIEW))
            }}
            onDeleteButtonClick={() => {
              dispatch(setSelectedCardId(card.id))
              dispatch(setAction(ActionEnum.DELETE))
            }}
          />
        ),
        width: 130,
      },
    ],
    [dispatch, filter, language]
  )

  return (
    <div style={{ height: '97.5%' }}>
      {management.showManageModal && <ManageCardModalComponent />}
      {showPayInModal && <PayInModalComponent />}
      <PageTitleComponent
        hasSearchDrawer
        onCreateButtonClick={() => {
          dispatch(setSelectedCardId())
          dispatch(setAction(ActionEnum.CREATE))
        }}
      />
      {management.showSearchDrawer && <SearchCardDrawerComponent />}
      <TableComponent
        data={cards}
        columns={columns}
        loading={!isCardsLoaded}
        paginationConfig={{
          total: totalCards,
          current: cardFilter.pageIndex,
        }}
        onChange={(pagination: TablePaginationConfig) => {
          const pageIndex = pagination.current || filter.pageIndex
          setFilter({ ...filter, pageIndex })
          dispatch(setCardFilter({ ...filter, pageIndex }))
        }}
      />
    </div>
  )
}
